import { ChangeEvent, useEffect, useState } from 'react';
import {
  OrdersAutoCancelExpirationTime,
  OrdersAutoCancelPaymentStatusConfiguration,
} from '@tiendanube/common/src/domains/orders/orders';
import { OrdersAutoCancelPaymentMethod } from '@tiendanube/common/src/enums';
import { CHECKOUT_AVG_EXPIRATION_MINUTES } from '../constants';

function usePaymentMethodsConfiguration(
  configuration: OrdersAutoCancelPaymentStatusConfiguration,
  disabled: boolean,
  onChange: (configuration: OrdersAutoCancelPaymentStatusConfiguration) => void,
) {
  const [paymentMethodsConfig, setPaymentMethodsConfig] =
    useState<OrdersAutoCancelPaymentStatusConfiguration>(configuration);

  const [selectedSubceedsCheckout, setSelectedSubceedsCheckout] =
    useState(false);

  // Track the original configuration to know when to reset expiration times
  // This should only be set once when the component first receives the configuration
  const [originalConfiguration, setOriginalConfiguration] =
    useState<OrdersAutoCancelPaymentStatusConfiguration | null>(null);

  // Track which payment methods should be focused after enabling toggle
  const [focusedPaymentMethods, setFocusedPaymentMethods] = useState<
    Set<OrdersAutoCancelPaymentMethod>
  >(new Set());

  const handleToggleChange = (
    e: ChangeEvent,
    paymentMethod: OrdersAutoCancelPaymentMethod,
    isActive: boolean,
  ): void => {
    if (disabled) {
      e.stopPropagation();
      e.preventDefault();
      return;
    }

    // Check if we're enabling a toggle that was previously disabled
    const wasDisabled = !paymentMethodsConfig[paymentMethod]?.isActive;
    const isBeingEnabled = isActive && wasDisabled;
    const isBeingDisabled = !isActive && !wasDisabled;

    // When disabling, check if we should reset expiration time to null
    let expirationTimeToSet =
      paymentMethodsConfig[paymentMethod]?.expirationTime;
    if (
      isBeingDisabled &&
      originalConfiguration &&
      (originalConfiguration[paymentMethod]?.expirationTime === null ||
        originalConfiguration[paymentMethod]?.expirationTime === undefined)
    ) {
      // Reset to null only if the original configuration had it as null/undefined
      expirationTimeToSet = null;
    }

    const newConfig = {
      ...paymentMethodsConfig,
      [paymentMethod]: {
        ...paymentMethodsConfig[paymentMethod],
        isActive,
        expirationTime: expirationTimeToSet,
      },
    };
    setPaymentMethodsConfig(newConfig);
    onChange(newConfig);

    // If toggle is being enabled (from disabled to enabled), mark for focus
    if (isBeingEnabled) {
      setFocusedPaymentMethods((prev) => new Set(prev).add(paymentMethod));
      // Clear focus state after the focus has been applied (using the same timeout pattern as useAutoFocus)
      setTimeout(() => {
        setFocusedPaymentMethods((prev) => {
          const newSet = new Set(prev);
          newSet.delete(paymentMethod);
          return newSet;
        });
      });
    }
  };

  const handleExpirationTimeChange = (
    paymentMethod: OrdersAutoCancelPaymentMethod,
    value: number,
  ): void => {
    const newConfig = {
      ...paymentMethodsConfig,
      [paymentMethod]: {
        ...paymentMethodsConfig[paymentMethod],
        expirationTime: value as OrdersAutoCancelExpirationTime,
      },
    };
    setPaymentMethodsConfig(newConfig);
    setSelectedSubceedsCheckout(
      Object.entries(newConfig).some(
        ([_, config]) =>
          config.isActive &&
          config.expirationTime &&
          config.expirationTime <= CHECKOUT_AVG_EXPIRATION_MINUTES,
      ),
    );
    onChange(newConfig);
  };

  useEffect(() => {
    setPaymentMethodsConfig(configuration);

    // Only set original configuration if it hasn't been set yet
    // This preserves the original state for comparison when toggling
    if (originalConfiguration === null) {
      setOriginalConfiguration(configuration);
    }

    setSelectedSubceedsCheckout(
      Object.entries(configuration).some(
        ([_, config]) =>
          config.isActive &&
          config.expirationTime &&
          config.expirationTime <= CHECKOUT_AVG_EXPIRATION_MINUTES,
      ),
    );
  }, [configuration, originalConfiguration]);

  return {
    paymentMethodsConfig,
    handleToggleChange,
    handleExpirationTimeChange,
    focusedPaymentMethods,
    selectedSubceedsCheckout,
  };
}

export default usePaymentMethodsConfiguration;
